import { ActivityActions } from "../../core/containers/Activity";
import { AutoplaySettingsActions } from "../../core/containers/AutoplaySettings";
import { DialogActions } from "../../core/containers/Dialogs";
import { FooterActions } from "../../core/containers/Footer";
import { HeaderActions } from "../../core/containers/Header";
import { KeypadActions } from "../../core/containers/Keypad";
import { MenuActions } from "../../core/containers/Menu";
import { MoneyActions } from "../../core/containers/Money";
import { ThemeActions } from "../../core/containers/Theme";
import { TickerDisplayActions } from "../../core/containers/TickerDisplay";
import {
  cleanDebugValue,
  isDebugActive,
  outputDebugMessage
} from "../../core/utils/debug";
import { showError } from "../../core/utils/errorHandler";
import { ErrorHandler } from "../../core/utils/errorHandler";
import { AutoplayButtonEvents } from "../../ui/AutoplayButton";
import { SpinButtonEvents } from "../../ui/SpinButton";
import { IEmitter, mitt } from "../lib/mitt";

export type LegacyEvent =
  | SpinButtonEvents
  | AutoplayButtonEvents
  | KeypadActions
  | MenuActions
  | AutoplaySettingsActions
  | MoneyActions
  | TickerDisplayActions
  | DialogActions
  | HeaderActions
  | FooterActions
  | ErrorHandler
  | ActivityActions
  | ThemeActions
  | "menubutton.click"
  | "gamerulesbutton.click"
  | "gamehistorybutton.click";

export type Callback = (event?: any) => void;

export const emitter: IEmitter = mitt(null);

export const API = {
  /**
   * Register an event handler for the given type.
   *
   * @param {String} type	Type of event to listen for, or `"*"` for all events
   * @param {Function} handler Function to call in response to given event
   * @memberof PlayUI.API
   */
  on: (event: LegacyEvent, callback: Callback) => {
    if (isDebugActive()) {
      outputDebugMessage("[API.on]", event, callback);
    }
    emitter.on(event, callback);
    return API;
  },

  /**
   * Remove an event handler for the given type.
   *
   * @param {String} type Type of event to unregister `handler` from, or `"*"`
   * @param {Function} handler Handler function to remove
   * @memberof PlayUI.API
   */
  off: (event: LegacyEvent, callback: Callback) => {
    if (isDebugActive()) {
      outputDebugMessage("[API.off]", event, callback);
    }
    emitter.off(event, callback);
    return API;
  },

  /**
   * Invoke all handlers for the given type.
   * If present, `"*"` handlers are invoked after type-matched handlers.
   *
   * @param {String} type The event type to invoke
   * @param {Any} [evt] Any value (object is recommended and powerful), passed to each handler
   * @memberof PlayUI.API
   */
  emit: (event: LegacyEvent, value?: any) => {
    // Since this is used externally we check if a listener actually exists. If not we show an error message.
    // This will avoid typos and deprecated events for example.
    if (emitter.listenerExists(event)) {
      emitter.emit(event, value);
    } else {
      showError("[API.emit] No listener exists for event: " + event);
    }

    if (isDebugActive()) {
      outputDebugMessage("[API.emit]", event, value);
    }
    return API;
  },
  /* Notify emitter does not require a listerner for emitting events */
  notify: (event: LegacyEvent, value: any) => {
    emitter.emit(event, value);

    if (isDebugActive()) {
      outputDebugMessage("[API.notify]", event, cleanDebugValue(value));
    }

    return API;
  },
  /* Print a list of all actions exposed by PlayUI */
  listActions: () => {
    console.info(JSON.stringify(emitter.listActions().sort(), null, 2));
    return API;
  }
};
